import { useEffect, useState } from 'react';
import { addOrderFee, fetchFeeProducts, type FeeProduct } from '../../lib/order-fees';

/**
 * Bijrijdertoeslag rijdt mee bij ophalen én afleveren → default aantal 2.
 * Andere toeslagen default 1.
 */
const BIJRIJDER_CODE = 'WAS-PRIM-BIJRIJDERTOESLAG';

interface AddFeeDialogProps {
  isOpen: boolean;
  orderId: string | null;
  onClose: () => void;
  onAdded: () => void;
}

export function AddFeeDialog({ isOpen, orderId, onClose, onAdded }: AddFeeDialogProps) {
  const [products, setProducts] = useState<FeeProduct[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProducts(null);
    fetchFeeProducts()
      .then((data) => {
        if (!cancelled) setProducts(data);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Toeslagen laden mislukt');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen || !orderId) return null;

  async function handleAdd(product: FeeProduct, quantity: number) {
    if (!orderId) return;
    setBusyId(product.id);
    setError(null);
    try {
      const res = await addOrderFee({ orderId, productId: product.id, quantity });
      if (!res.success) {
        setError(res.message || 'Toevoegen mislukt');
        return;
      }
      onAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Toeslag / kosten toevoegen</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Prijs wordt automatisch berekend na toevoegen.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">
              {error}
            </div>
          )}

          {!loading && products && products.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6">
              Geen toeslag-producten gevonden.
            </p>
          )}

          {!loading && products && products.length > 0 && (
            <ul className="space-y-2">
              {products.map((p) => (
                <FeeRow
                  key={p.id}
                  product={p}
                  busy={busyId !== null}
                  isThisBusy={busyId === p.id}
                  onAdd={handleAdd}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

function FeeRow({
  product,
  busy,
  isThisBusy,
  onAdd,
}: {
  product: FeeProduct;
  busy: boolean;
  isThisBusy: boolean;
  onAdd: (product: FeeProduct, quantity: number) => void;
}) {
  const [quantity, setQuantity] = useState(product.code === BIJRIJDER_CODE ? 2 : 1);

  return (
    <li className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 hover:border-indigo-300">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900">{product.name}</div>
        <p className="text-[11px] text-slate-400 font-mono mt-0.5">{product.code}</p>
      </div>
      <label className="flex items-center gap-1.5 text-xs text-slate-500 flex-shrink-0">
        Aantal
        <input
          type="number"
          min={1}
          max={20}
          step={1}
          value={quantity}
          disabled={busy}
          onChange={(e) => {
            const n = Math.max(1, Math.min(20, Number(e.target.value) || 1));
            setQuantity(n);
          }}
          className="w-16 border border-slate-300 rounded px-2 py-1 text-sm bg-white disabled:opacity-60"
        />
      </label>
      <button
        type="button"
        disabled={busy}
        onClick={() => onAdd(product, quantity)}
        className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300 flex-shrink-0"
      >
        {isThisBusy ? 'Bezig…' : 'Toevoegen'}
      </button>
    </li>
  );
}
